import React, { useState } from 'react';
import { CheckCircle, AlertTriangle, ShieldOff, ChevronDown, ChevronUp, FileText, X } from 'lucide-react';

export interface CitationVerificationResult {
  status?: string;
  skipped_reason?: string;
  total_claims?: number;
  verified?: number;
  likely?: number;
  unverified?: number;
  verification_rate?: number;
  accuracy_score?: number;
  llm_calls_used?: number;
  claims?: Array<{
    claim: string;
    status: 'verified' | 'likely' | 'unverified';
    match_score: number;
    evidence_snippet?: string | null;
    source_ref?: {
      id?: string | null;
      document_id?: number | null;
      document_name?: string | null;
      page?: number | null;
      section?: string | null;
    } | null;
    llm_verdict?: { entails: boolean; reason?: string | null } | null;
  }>;
  latency_ms?: number;
  generated_at?: string;
  error?: string;
}

interface SourceTracePanelProps {
  citation?: CitationVerificationResult | null;
  onClose?: () => void;
  embedded?: boolean;
}

const statusConfig = {
  verified: { Icon: CheckCircle, color: 'text-green-700', bg: 'bg-green-50', border: 'border-green-200', label: 'Verified' },
  likely: { Icon: AlertTriangle, color: 'text-amber-700', bg: 'bg-amber-50', border: 'border-amber-200', label: 'Likely' },
  unverified: { Icon: ShieldOff, color: 'text-red-700', bg: 'bg-red-50', border: 'border-red-200', label: 'Unverified' }
} as const;

const SourceTracePanel: React.FC<SourceTracePanelProps> = ({ citation, onClose, embedded = false }) => {
  const [expandedClaims, setExpandedClaims] = useState<Set<number>>(new Set());
  const [filter, setFilter] = useState<'all' | 'verified' | 'likely' | 'unverified'>('all');

  if (!citation) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm text-gray-600">
        No citation verification data available.
      </div>
    );
  }

  if (citation.status === 'skipped') {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm text-gray-600">
        <div className="flex items-center gap-2 font-medium text-gray-700 mb-1">
          <FileText className="w-4 h-4" />
          Citation Verification Skipped
        </div>
        <div className="text-xs text-gray-500">{citation.skipped_reason || 'No source documents were available for verification.'}</div>
      </div>
    );
  }

  if (citation.status === 'error') {
    return (
      <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 text-sm text-orange-700">
        Citation verification error: {citation.error || 'unknown'}
      </div>
    );
  }

  const claims = citation.claims || [];
  const filteredClaims = filter === 'all' ? claims : claims.filter(c => c.status === filter);

  const toggleClaim = (idx: number) => {
    const next = new Set(expandedClaims);
    if (next.has(idx)) next.delete(idx);
    else next.add(idx);
    setExpandedClaims(next);
  };

  const containerClass = embedded
    ? 'bg-white border border-gray-200 rounded-lg'
    : 'bg-white border border-gray-200 rounded-lg shadow-lg';

  return (
    <div className={containerClass}>
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        <div className="flex items-center gap-3">
          <FileText className="w-5 h-5 text-indigo-600" />
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Source Trace (CiteFix)</h3>
            <p className="text-xs text-gray-500">Citation accuracy: {citation.accuracy_score ?? 0}/100</p>
          </div>
        </div>
        {onClose && (
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100">
            <X className="w-4 h-4 text-gray-500" />
          </button>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-4 bg-gray-50 border-b border-gray-200">
        <div>
          <div className="text-xs text-gray-500">Total Claims</div>
          <div className="text-lg font-semibold text-gray-900">{citation.total_claims ?? 0}</div>
        </div>
        <div>
          <div className="text-xs text-gray-500">Verified</div>
          <div className="text-lg font-semibold text-green-700">{citation.verified ?? 0}</div>
        </div>
        <div>
          <div className="text-xs text-gray-500">Likely</div>
          <div className="text-lg font-semibold text-amber-700">{citation.likely ?? 0}</div>
        </div>
        <div>
          <div className="text-xs text-gray-500">Unverified</div>
          <div className="text-lg font-semibold text-red-700">{citation.unverified ?? 0}</div>
        </div>
      </div>

      <div className="px-4 py-2 border-b border-gray-200 flex items-center gap-2 text-xs">
        <span className="text-gray-500">Filter:</span>
        {(['all', 'verified', 'likely', 'unverified'] as const).map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-2 py-0.5 rounded ${filter === f ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
          >
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      <div className="max-h-96 overflow-y-auto">
        {filteredClaims.length === 0 ? (
          <div className="p-4 text-sm text-gray-500 text-center">No claims match this filter.</div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {filteredClaims.map((claim, i) => {
              const cfg = statusConfig[claim.status] || statusConfig.unverified;
              const expanded = expandedClaims.has(i);
              return (
                <li key={i} className="p-3">
                  <button onClick={() => toggleClaim(i)} className="w-full flex items-start gap-2 text-left">
                    <cfg.Icon className={`w-4 h-4 ${cfg.color} flex-shrink-0 mt-0.5`} />
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-gray-900 leading-snug">{claim.claim}</div>
                      <div className="flex items-center gap-2 mt-1 text-[10px] text-gray-500">
                        <span className={`px-1.5 py-0.5 rounded ${cfg.bg} ${cfg.color}`}>{cfg.label}</span>
                        <span>match {(claim.match_score * 100).toFixed(0)}%</span>
                        {claim.source_ref?.document_name && (
                          <span className="truncate">{claim.source_ref.document_name}{claim.source_ref.page ? ` · p.${claim.source_ref.page}` : ''}</span>
                        )}
                      </div>
                    </div>
                    {expanded ? <ChevronUp className="w-4 h-4 text-gray-400 flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />}
                  </button>
                  {expanded && (
                    <div className="mt-2 ml-6 pl-3 border-l-2 border-gray-200 space-y-2 text-xs">
                      {claim.evidence_snippet && (
                        <div>
                          <div className="text-gray-500">Evidence:</div>
                          <div className="text-gray-800 italic bg-gray-50 p-2 rounded">"{claim.evidence_snippet}"</div>
                        </div>
                      )}
                      {claim.source_ref && (
                        <div className="text-gray-500">
                          Source: {claim.source_ref.document_name || 'unknown'}
                          {claim.source_ref.page && ` · page ${claim.source_ref.page}`}
                          {claim.source_ref.section && ` · ${claim.source_ref.section}`}
                        </div>
                      )}
                      {claim.llm_verdict && (
                        <div>
                          <div className="text-gray-500">LLM entailment check:</div>
                          <div className={claim.llm_verdict.entails ? 'text-green-700' : 'text-red-700'}>
                            {claim.llm_verdict.entails ? '✓ Entails' : '✗ Does not entail'}
                            {claim.llm_verdict.reason && ` — ${claim.llm_verdict.reason}`}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div className="px-4 py-2 border-t border-gray-200 text-[10px] text-gray-500 flex items-center justify-between">
        <span>{citation.llm_calls_used ?? 0} LLM fallback call(s) · {citation.latency_ms ?? 0}ms</span>
        <span>{((citation.verification_rate ?? 0) * 100).toFixed(0)}% direct verification rate</span>
      </div>
    </div>
  );
};

export default SourceTracePanel;
